import { useState } from 'react';

const PROFESSIONAL_TEMPLATES = [
  {
    name: 'Authorized Welcome',
    category: 'System',
    message: `🎉 Welcome to [Your Business Name] Protocol!\n\nEntities integrated into our system receive:\n✅ Premium assets\n✅ Rapid synchronization\n✅ 24/7 technical oversight\n\nInitializing engagement sequence...`,
    buttonText: 'Initialize Protocol',
    buttonUrl: 'https://example.com'
  },
  {
    name: 'Pulse Alert',
    category: 'Marketing',
    message: `🔥 CRITICAL OPPORTUNITY! 🔥\n\nScale of engagement exceeds 70% reduction in resource allocation.\n\n⏰ Temporary Window Active\n📦 Priority Logistics Included\n💳 Seamless Resolution\n\nExecute command now.`,
    buttonText: 'Acquire Assets',
    buttonUrl: 'https://example.com/sale'
  },
  {
    name: 'Transaction Integrity',
    category: 'E-commerce',
    message: `✅ Transaction Log Verified!\n\nOrder Hash: #[ORDER_ID]\n\n📦 Ingestion: Progressing\n🚚 Final Sync: [DATE]\n💰 Resolved: ₹[AMOUNT]\n\nTracking markers and active alerts enabled.`,
    buttonText: 'Trace Logistics',
    buttonUrl: 'https://example.com/track'
  }
];

const EMOJI_CATEGORIES = {
  'Protocol': ['💼', '📊', '📈', '💰', '🏢', '📱', '💻', '📧'],
  'Resource': ['🛍️', '🛒', '💳', '📦', '🎁', '💝', '🏷️', '💎'],
  'Entity': ['🎉', '🎊', '🎈', '🎂', '🎆', '🎇', '✨', '🌟'],
  'Chronos': ['⏰', '⏱️', '📅', '📆', '🕐', '⌚', '⏳', '⏲️'],
  'Node': ['📍', '🗺️', '🏠', '🏢', '🏪', '🏬', '🏭', '🏛️'],
  'Signal': ['📱', '📞', '📧', '💬', '📨', '📩', '✉️', '📬'],
  'Success': ['✅', '✔️', '👍', '💯', '🎯', '🏆', '🥇', '⭐'],
  'Alert': ['⚠️', '🔔', '🔊', '📢', '🚨', '❗', '❓', '💡']
};

const TemplateEditor = ({ initialData, onSave, onCancel }) => {
  const [formData, setFormData] = useState(initialData || {
    name: '',
    message: '',
    buttonText: '',
    buttonUrl: '',
    imageUrl: ''
  });

  const [showTemplates, setShowTemplates] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Protocol');

  const insertEmoji = (emoji) => {
    const textarea = document.getElementById('message-textarea');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.message;
    const newText = text.substring(0, start) + emoji + text.substring(end);
    setFormData({ ...formData, message: newText });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('message-textarea');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.message;
    const newText = text.substring(0, start) + variable + text.substring(end);
    setFormData({ ...formData, message: newText });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  const applyTemplate = (template) => {
    setFormData({
      name: template.name,
      message: template.message,
      buttonText: template.buttonText,
      buttonUrl: template.buttonUrl
    });
    setShowTemplates(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
      {/* Configuration Hub */}
      <div className="lg:col-span-7 space-y-8 animate-in slide-in-from-left-4 duration-500">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Header context */}
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-bulky-blue-500 text-white flex items-center justify-center shadow-lg shadow-bulky-blue-500/20">
              <span className="material-icons-round text-2xl">settings_input_component</span>
            </div>
            <div>
              <h2 className="font-medium">Protocol Parameters</h2>
              <p className="text-[10px] font-normal text-gray-400 uppercase tracking-wider mt-1">Define the template logic and content</p>
            </div>
          </div>

          {/* Template Designation */}
          <div className="space-y-3">
            <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider ml-1">Template Code/Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., PROTOCOL-VERIFICATION-ALPHA"
              required
              className="w-full px-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-[1.25rem] focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
            />
          </div>

          {/* Template Library Hook */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowTemplates(!showTemplates)}
              className={`w-full py-4.5 px-6 rounded-2xl flex items-center justify-between transition-all font-normal text-[10px] uppercase tracking-wider border-2 shadow-sm ${showTemplates ? 'bg-bulky-navy-800 text-white border-bulky-navy-800' : 'bg-white text-bulky-navy-600 border-gray-100 hover:border-bulky-blue-200'}`}
            >
              <div className="flex items-center gap-3">
                <span className="material-icons-round text-lg">library_books</span>
                <span>Access Pre-defined Protocols</span>
              </div>
              <span className={`material-icons-round transition-transform duration-500 ${showTemplates ? 'rotate-180' : ''}`}>expand_more</span>
            </button>

            {showTemplates && (
              <div className="mt-3 absolute top-full left-0 w-full z-20 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 space-y-2 max-h-72 overflow-y-auto animate-in fade-in slide-in-from-top-4 duration-300">
                {PROFESSIONAL_TEMPLATES.map((template, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => applyTemplate(template)}
                    className="w-full text-left p-5 hover:bg-bulky-navy-50 rounded-xl transition-all border-2 border-transparent hover:border-bulky-blue-100 group"
                  >
                    <div className="font-normal text-bulky-navy-800 text-xs uppercase tracking-tight group-hover:text-bulky-blue-500">{template.name}</div>
                    <div className="text-[9px] font-normal text-gray-400 mt-1 uppercase tracking-wider flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-bulky-blue-400"></span>
                      {template.category}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Core Content Logic */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
              <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider ml-1">Payload Content (Message) *</label>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setShowEmojis(!showEmojis)}
                  className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-[10px] font-normal uppercase tracking-wider transition-all ${showEmojis ? 'bg-yellow-400 text-bulky-navy-800 shadow-lg shadow-yellow-400/20' : 'bg-gray-100 text-gray-500 hover:text-bulky-navy-600'}`}
                >
                  <span className="material-icons-round text-sm">mood</span>
                  {showEmojis ? 'Active' : 'Symbols'}
                </button>
                <div className="hidden sm:block h-6 w-px bg-gray-200"></div>
                <button
                  type="button"
                  onClick={() => insertVariable('[NAME]')}
                  className="flex-1 sm:flex-none bg-bulky-navy-50 hover:bg-bulky-navy-100 text-bulky-navy-600 hover:text-bulky-blue-500 px-4 py-2 rounded-xl text-[10px] font-normal uppercase tracking-wider transition-all flex items-center justify-center gap-2 border border-bulky-navy-100/50"
                >
                  <span className="material-icons-round text-sm">alternate_email</span>
                  Variable
                </button>
              </div>
            </div>

            {showEmojis && (
              <div className="bg-white rounded-[2rem] p-6 border-2 border-yellow-200 shadow-2xl animate-in zoom-in-95 duration-300">
                <div className="flex gap-2 mb-6 p-1.5 bg-gray-50 rounded-xl overflow-x-auto no-scrollbar">
                  {Object.keys(EMOJI_CATEGORIES).map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-4 py-2 rounded-lg text-[9px] font-normal uppercase tracking-wider whitespace-nowrap transition-all ${selectedCategory === cat
                        ? 'bg-white shadow-sm text-bulky-navy-800 text-bulky-blue-500'
                        : 'text-gray-400 hover:text-bulky-navy-600'
                        }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 md:gap-3">
                  {EMOJI_CATEGORIES[selectedCategory].map((emoji, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => insertEmoji(emoji)}
                      className="text-2xl hover:bg-yellow-50 rounded-xl p-3 transition-all transform hover:scale-125 flex items-center justify-center grayscale hover:grayscale-0"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="relative group">
              <textarea
                id="message-textarea"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Initialize payload sequence..."
                rows="8"
                required
                className="w-full px-6 py-6 bg-gray-50 border-2 border-transparent rounded-[1.5rem] focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-sans font-normal text-bulky-navy-800 leading-relaxed shadow-sm min-h-[280px]"
              />
              <div className="absolute bottom-4 right-6 text-[9px] font-normal text-bulky-blue-500 uppercase tracking-wider bg-white/90 backdrop-blur px-3 py-1.5 rounded-full border border-bulky-blue-100 shadow-sm">
                Density: {formData.message.length}
              </div>
            </div>
          </div>

          {/* Action Trigger Config */}
          <div className="bg-bulky-navy-50 rounded-[2.5rem] border border-bulky-navy-100/50 p-6 md:p-8 space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-bulky-blue-500 shadow-sm">
                <span className="material-icons-round text-sm">touch_app</span>
              </div>
              <h2 className="font-medium">Interactivity Layer (Action Hub)</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider ml-1">Button Command Text</label>
                <input
                  type="text"
                  value={formData.buttonText}
                  onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                  placeholder="e.g., SYNC-ASSETS-NOW"
                  className="w-full px-5 py-4 bg-white border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                />
              </div>

              <div className="space-y-3">
                <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider ml-1">Command URL</label>
                <input
                  type="url"
                  value={formData.buttonUrl}
                  onChange={(e) => setFormData({ ...formData, buttonUrl: e.target.value })}
                  placeholder="https://protocol.io/exec"
                  className="w-full px-5 py-4 bg-white border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider ml-1">Visual Asset URL (Card Image)</label>
              <div className="relative group">
                <span className="material-icons-round absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-bulky-blue-500">image</span>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  placeholder="https://static.network/asset-01.jpg"
                  className="w-full pl-14 pr-6 py-4 bg-white border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                />
              </div>
              <p className="text-[9px] font-normal text-bulky-blue-500 uppercase tracking-wider flex items-center gap-1.5 ml-1">
                <span className="material-icons-round text-xs">auto_awesome</span>
                Provisioning an image URL enables high-fidelity rich preview cards.
              </p>
            </div>
          </div>

          {/* Action Matrix */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-8 py-4.5 border-2 border-gray-100 text-bulky-navy-500 font-normal rounded-2xl hover:bg-gray-50 transition-all uppercase tracking-wider text-[10px]"
            >
              Abort
            </button>
            <button
              type="submit"
              className="flex-1 px-8 py-4.5 bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal rounded-2xl shadow-xl shadow-bulky-blue-500/30 active:scale-[0.98] transition-all uppercase tracking-wider text-[10px] flex items-center justify-center gap-3"
            >
              <span className="material-icons-round text-lg">save</span>
              Commit to Registry
            </button>
          </div>
        </form>
      </div>

      {/* Real-time Render Matrix */}
      <div className="lg:col-span-5 animate-in slide-in-from-right-4 duration-500">
        <div className="lg:sticky lg:top-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-medium">Protocol Preview</h2>
              <p className="text-[9px] font-normal text-gray-400 uppercase tracking-wider mt-1">Real-time Node Rendering</p>
            </div>
            <div className="flex gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400 shadow-lg shadow-red-400/20"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400 shadow-lg shadow-yellow-400/20"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-lg shadow-emerald-400/20"></div>
            </div>
          </div>

          <div className="bg-[#e5ddd5] rounded-[2.5rem] md:rounded-[3rem] p-6 md:p-8 shadow-2xl relative overflow-hidden border-8 border-bulky-navy-50 md:border-[12px]">
            {/* WhatsApp Background Subtle Pattern */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cubes.png")' }}></div>

            {/* WhatsApp Context Interface */}
            <div className="bg-bulky-navy-800 text-white p-4.5 rounded-[1.25rem] md:rounded-[1.5rem] flex items-center justify-between shadow-xl mb-8 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-bulky-blue-500/10 rounded-full -mr-12 -mt-12 transition-transform duration-700 group-hover:scale-110"></div>
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center text-xl md:text-2xl shadow-inner transform transition-transform group-hover:scale-105">
                  🏢
                </div>
                <div>
                  <div className="font-normal text-[10px] md:text-xs uppercase tracking-wider">Network HQ</div>
                  <div className="text-[8px] md:text-[9px] font-normal text-bulky-blue-300 flex items-center gap-1.5 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                    Terminal Sync: Active
                  </div>
                </div>
              </div>
              <div className="flex gap-3 text-white/40 relative z-10">
                <span className="material-icons-round text-lg md:text-xl">videocam</span>
                <span className="material-icons-round text-lg md:text-xl">call</span>
                <span className="material-icons-round text-lg md:text-xl">more_vert</span>
              </div>
            </div>

            {/* Encrypted Message Capsule */}
            <div className="space-y-1 relative max-w-[90%] md:max-w-[85%]">
              <div className="absolute left-[-10px] top-0 text-white">
                <svg width="10" height="15" viewBox="0 0 10 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 0H10V15C10 15 8 13.5 0 0Z" fill="white" />
                </svg>
              </div>

              <div className="bg-white rounded-2xl rounded-tl-none shadow-lg overflow-hidden border border-gray-100/50">
                {/* Visual Asset Ingestion */}
                {formData.imageUrl && (
                  <div className="relative aspect-video group overflow-hidden bg-gray-100">
                    <img
                      src={formData.imageUrl}
                      alt="Protocol Asset"
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                      onError={(e) => e.target.closest('.aspect-video').style.display = 'none'}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60"></div>
                  </div>
                )}

                <div className="p-4 md:p-6">
                  <div className="whitespace-pre-wrap text-bulky-navy-800 text-xs md:text-[13px] leading-relaxed font-sans font-normal mb-4">
                    {formData.message || 'Initializing payload content preview...'}
                  </div>

                  {/* Interactivity Hook */}
                  {formData.buttonText && (
                    <div className="border-t border-gray-100 pt-4 mt-2">
                      <div className="group relative flex items-center justify-center gap-2 bg-bulky-blue-50 border border-bulky-blue-100 text-bulky-blue-600 font-normal py-3 px-5 rounded-xl transition-all cursor-default hover:bg-bulky-blue-100">
                        <span className="material-icons-round text-sm">open_in_new</span>
                        <span className="text-[10px] md:text-[11px] font-normal uppercase tracking-wider">{formData.buttonText}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-1.5 mt-4 opacity-40">
                    <span className="text-[9px] font-normal text-bulky-navy-800">
                      {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="material-icons-round text-[12px] text-bulky-blue-500">done_all</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Neural Net Hint */}
            <div className="mt-10 bg-bulky-navy-800/10 backdrop-blur-xl rounded-[1.5rem] p-5 border border-white/60 relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-bulky-blue-500"></div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-lg bg-bulky-blue-500 flex items-center justify-center text-white shrink-0 shadow-lg shadow-bulky-blue-500/20 transform transition-transform group-hover:rotate-12">
                  <span className="material-icons-round text-lg">psychology</span>
                </div>
                <p className="text-[9px] font-normal text-bulky-navy-800/70 leading-relaxed uppercase tracking-wider">
                  Intelligence Matrix: This preview simulates a high-fidelity WhatsApp decryption. Variations may occur based on terminal resolution and protocol version.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
